import React, { useState, useEffect, useRef } from "react";
import { Table, Pagination, Select } from "antd";
import BaseEmpty from "./BaseEmpty";
import { TABLE_CARD_BREAKPOINT } from "@theme/constants";
import "./layouts-styles.css";

/**
 * The "items per page" control, with typing turned off.
 *
 * antd's own size changer leaves its input editable, so it took a
 * caret and accepted free text — typing "a" into it looked like a page
 * number field happily eating a letter. It is a list of fixed page
 * sizes; there is nothing to search and nothing to type.
 *
 * rc-pagination renders `selectComponentClass` and reaches for its
 * `Option` static, so that has to come along.
 */
const PageSizeSelect = (props) => <Select { ...props } showSearch={ false } />;
PageSizeSelect.Option = Select.Option;

/** Shimmer rows inside the real table frame, so nothing jumps on arrival. */
const TableSkeleton = ({ columns, rows = 8 }) => (
  <div className="table-skeleton">
    { Array.from({ length: rows }).map((_, row) => (
      <div className="table-skeleton-row" key={ row }>
        { columns.map((column, cell) => (
          <span
            key={ cell }
            className="table-skeleton-cell"
            style={ {
              flex: cell === 0 ? '1.6 1 0' : '1 1 0',
              animationDelay: `${row * 60}ms`,
            } }
          />
        )) }
      </div>
    )) }
  </div>
);

/**
 * Stacked cards for narrow screens.
 *
 * A twelve-column table on a phone is a horizontal scrollbar with data
 * hidden inside it. Below 768px each row becomes a labelled card
 * instead, using the same columns and the same status spine.
 */
const TableCards = ({ columns, dataSource, rowClassName, pagination, onChange }) => {
  const visible = columns.filter((column) => column.title);

  // The first column identifies the row and the actions column acts on it,
  // so both belong in a header band. Labelling them ("PATIENT: Ayesha") only
  // repeats what the value already makes obvious.
  const lead = visible[0];
  const actions = visible.find((column) => column.key === 'actions');
  const details = visible.filter((column) => column !== lead && column !== actions);

  const cellValue = (column, record, index) => column.render
    ? column.render(column.dataIndex ? record[column.dataIndex] : record, record, index)
    : record[column.dataIndex];

  return (
    <div className="table-cards">
      { dataSource.map((record, index) => (
        <div
          key={ record.id || index }
          className={ `table-card ${typeof rowClassName === 'function' ? rowClassName(record, index) : ''}` }
        >
          <div className="table-card-head">
            <div className="table-card-lead">{ lead && cellValue(lead, record, index) }</div>
            { actions && <div className="table-card-actions">{ cellValue(actions, record, index) }</div> }
          </div>

          { details.map((column, i) => {
            const value = cellValue(column, record, index);
            if (value === null || value === undefined || value === '') return null;
            return (
              <div className="table-card-row" key={ i }>
                <span className="table-card-label">{ column.title }</span>
                <span className="table-card-value">{ value }</span>
              </div>
            );
          }) }
        </div>
      )) }

      { pagination &&
        <div className="table-cards-pagination">
          <Pagination
            simple
            current={ pagination.current }
            pageSize={ pagination.pageSize }
            total={ pagination.total }
            onChange={ (page, pageSize) =>
              onChange && onChange({ current: page, pageSize }, {}, {}) }
          />
        </div> }
    </div>
  );
};

/**
 * The app's table.
 *
 * Adds to antd's Table: a first-load skeleton instead of a spinner over
 * an empty box, a purpose-written empty state, the status spine, and a
 * card layout on narrow screens.
 *
 * Extra props:
 *   emptyState   { icon, title, description, action } for BaseEmpty
 *   spineOf      (record) => tone class, paints the leading-edge bar
 *   cardsOnMobile  set false to keep the scrolling table on phones
 *   scrollDisabled set true for a table that must fit its container
 *                  rather than scroll sideways (pair with tableLayout)
 */
const BaseTable = (props) => {
  const {
    columns,
    loader,
    emptyState,
    spineOf,
    rowClassName,
    cardsOnMobile = true,
    scrollDisabled,
    ...otherProps
  } = props;

  const [compact, setCompact] = useState(
    typeof window !== "undefined" && window.innerWidth < TABLE_CARD_BREAKPOINT
  );

  useEffect(() => {
    let frame = null;
    const onResize = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        setCompact(window.innerWidth < TABLE_CARD_BREAKPOINT);
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const data = otherProps.dataSource || [];

  // A page's first request starts in an effect, i.e. after the first
  // paint — so without this the empty state flashes for one frame before
  // the loader ever goes true. A table given no `loader` at all is
  // treated as already settled, so static tables still render normally.
  const started = useRef(loader === undefined);
  if (loader) started.current = true;

  const firstLoad = (loader || !started.current) && data.length === 0;

  const resolveRowClassName = (record, index) => {
    const own = typeof rowClassName === 'function' ? rowClassName(record, index) : (rowClassName || '');
    const spine = spineOf ? `spine-row ${spineOf(record)}` : '';
    return `${own} ${spine}`.trim();
  };

  if (firstLoad) {
    return <TableSkeleton columns={ columns } />;
  }

  if (!loader && data.length === 0) {
    return <BaseEmpty { ...(emptyState || {}) } />;
  }

  if (compact && cardsOnMobile) {
    return (
      <TableCards
        columns={ columns }
        dataSource={ data }
        rowClassName={ resolveRowClassName }
        pagination={ otherProps.pagination }
        onChange={ otherProps.onChange }
      />
    );
  }

  return (
    <Table
      rowKey="id"
      size="middle"
      className="base-table"
      { ...otherProps }
      pagination={ otherProps.pagination === false
        ? false
        : { ...otherProps.pagination, selectComponentClass: PageSizeSelect } }
      columns={ columns }
      rowClassName={ resolveRowClassName }
      showSorterTooltip={ false }
      // 'max-content' sizes the table to its widest unwrapped content,
      // which is right for a data grid and wrong for a table whose cells
      // are meant to wrap — that one has to be able to opt out entirely
      // rather than be handed a scroll setting it cannot satisfy.
      scroll={ scrollDisabled ? undefined : { x: 'max-content' } }
      loading={ loader }
    />
  );
};

export default BaseTable;
